import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, ArrowUpRight } from 'lucide-react';

const footerLinks = {
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'Our Team', path: '/about#team' },
    { name: 'Philosophy', path: '/about#philosophy' },
    { name: 'Contact', path: '/contact' },
  ],
  services: [
    { name: 'Architectural Design', path: '/services#architectural-design' },
    { name: 'Interior Design', path: '/services#interior-design' },
    { name: 'PMC Services', path: '/services#project-management' },
    { name: 'Redevelopment Advisory', path: '/services#redevelopment-advisory' },
  ],
  portfolio: [
    { name: 'PMC Projects', path: '/portfolio#pmc' },
    { name: 'Interior Projects', path: '/portfolio#interior' },
    { name: 'Featured Work', path: '/portfolio' },
  ],
  knowledge: [
    { name: 'Self Redevelopment', path: '/knowledge-hub/self-redevelopment' },
    { name: '79A Process', path: '/knowledge-hub/79a-process' },
    { name: 'Funding Options', path: '/knowledge-hub/funding-options' },
    { name: 'Society Guide', path: '/knowledge-hub/society-guide' },
  ],
};

const Footer = () => {
  const whatsappLink = "https://wa.me/917977482624?text=Hello%2C%20I'm%20interested%20in%20your%20services.";
  const location = useLocation();
  const navigate = useNavigate();

  const handleHashLinkClick = (e, path) => {
    e.preventDefault();
    const [pathname, hash] = path.split('#');
    
    if (location.pathname === pathname && hash) {
      const element = document.getElementById(hash);
      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    } else {
      navigate(path);
    }
  };

  return (
    <footer className="bg-card border-t border-white/5" data-testid="main-footer">
      {/* CTA Section */}
      <div className="arch-container py-20">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 pb-16 border-b border-white/10">
          <div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-4">
              Ready to Transform<br />
              <span className="text-gold">Your Vision?</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-md">
              Let's discuss how we can bring your real estate project to life.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-gold text-black hover:bg-gold-light px-8 py-4 text-sm uppercase tracking-widest font-bold transition-all duration-300"
              data-testid="footer-consultation-btn"
            >
              Schedule Consultation
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white hover:bg-white hover:text-black px-8 py-4 text-sm uppercase tracking-widest font-medium transition-all duration-300"
              data-testid="footer-whatsapp-btn"
            >
              WhatsApp Us
            </a>
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 py-16">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-8 lg:mb-0">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/av-logo-64.png" 
                alt="Agastya Ventures" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Your trusted, start-to-end real estate partner, delivering comprehensive solutions across India's residential and commercial markets.
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="tel:+917977482624"
                className="flex items-center gap-3 text-white/70 hover:text-gold transition-colors text-sm"
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                +91 79774 82624
              </a>
              <a
                href="mailto:info@agastyaventures.com"
                className="flex items-center gap-3 text-white/70 hover:text-gold transition-colors text-sm"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                info@agastyaventures.com
              </a>
              <div className="flex items-start gap-3 text-white/70 text-sm">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" strokeWidth={1.5} />
                Mumbai, Maharashtra, India
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white font-bold mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={(e) => link.path.includes('#') ? handleHashLinkClick(e, link.path) : undefined}
                    className="text-muted-foreground hover:text-gold transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white font-bold mb-6">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={(e) => link.path.includes('#') ? handleHashLinkClick(e, link.path) : undefined}
                    className="text-muted-foreground hover:text-gold transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Portfolio Links */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white font-bold mb-6">Portfolio</h3>
            <ul className="space-y-3">
              {footerLinks.portfolio.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={(e) => link.path.includes('#') ? handleHashLinkClick(e, link.path) : undefined}
                    className="text-muted-foreground hover:text-gold transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Knowledge Links */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-white font-bold mb-6">Knowledge Hub</h3>
            <ul className="space-y-3">
              {footerLinks.knowledge.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-muted-foreground hover:text-gold transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Agastya Ventures. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-muted-foreground hover:text-gold transition-colors text-sm">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-muted-foreground hover:text-gold transition-colors text-sm">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <motion.a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg shadow-black/30"
        data-testid="floating-whatsapp-btn"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </motion.a>
    </footer>
  );
};

export default Footer;
